import React, { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, useLocation, useParams, Navigate } from "react-router-dom";
import Lenis from "lenis";
import Home from "./pages/Home";
import Work from "./pages/Work";
import CaseStudy from "./pages/CaseStudy";
import About from "./pages/About";
import Playground from "./pages/Playground";
import Contact from "./pages/Contact";
import NotFound from "./pages/NotFound";
import { Header, StatusBar, Footer } from "./components/layout";
import ShowreelOverlay from "./components/Showreel";
import { Lightbox } from "./components/Lightbox";
import { slugRedirects } from "./data/portfolio";

function useSmoothScroll() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); window.__lenis = null; };
  }, []);
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// 301-style redirect for legacy /project/:slug links.
function ProjectRedirect() {
  const { slug } = useParams();
  const target = slugRedirects[slug] || slug;
  return <Navigate to={`/work/${target}`} replace />;
}

function Shell() {
  const location = useLocation();
  useSmoothScroll();
  const isCaseStudy = /^\/work\/[^/]+$/.test(location.pathname);

  return (
    <div className="min-h-screen flex flex-col relative" style={{ background: "var(--paper)" }}>
      <ScrollToTop />
      <Header />
      <ShowreelOverlay />
      <main className="flex-1">
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/work" element={<Work />} />
          <Route path="/work/:slug" element={<CaseStudy />} />
          <Route path="/about" element={<About />} />
          <Route path="/playground" element={<Playground />} />
          <Route path="/contact" element={<Contact />} />
          {/* legacy redirects */}
          <Route path="/archive" element={<Navigate to="/work" replace />} />
          <Route path="/project/:slug" element={<ProjectRedirect />} />
          <Route path="/chaos" element={<Navigate to="/work/chaos-command-center" replace />} />
          <Route path="/playground/:slug" element={<Navigate to="/playground" replace />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      {!isCaseStudy && <Footer />}
      <StatusBar />
      <Lightbox />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
